import { useState, createContext, useContext } from 'react';
import { translationEN } from './locales/en/translationEN';
import { translationDE } from './locales/de/translationDE';

import { Globe, Check } from 'lucide-react';
import {
  Typography,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  useTheme,
} from '@mui/material';
//import { useTranslation, languages } from '../contexts/I18nContext';


const I18nContext = createContext(null);

// Define available languages
export const languages = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  // { code: 'es', label: 'Español', flag: '🇪🇸' },
  // { code: 'fr', label: 'Français', flag: '🇫🇷' },
];

const translations = {
  en: translationEN,
  de: translationDE,
};

export const I18nProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) return key;
    }
    
    return value || key;
  };

  const changeLanguage = (lng) => {
    if (translations[lng]) {
      setLanguage(lng);
    } else {
      console.warn(`Language "${lng}" not found. Available languages:`, Object.keys(translations));
    }
  };

  const value = {
    language,
    t,
    changeLanguage,
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }
  return context;
};


export default function LanguageSelector() {
  const { language, changeLanguage } = useTranslation();
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (languageCode) => {
    changeLanguage(languageCode);
    handleClose();
  };

  const currentLanguage = languages.find(
    (lang) => lang.code === language
  ) || languages[0];

  return (
    <>
      <Tooltip title="Select Language" placement="right">
        <IconButton
          onClick={handleClick}
          aria-controls={open ? 'language-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Globe size={18} />
          <Typography
            component="span"
            variant="subtitle2"
            sx={{ pt: 0.2, fontWeight: 600 }}
          >
            &nbsp;{currentLanguage.code.toUpperCase()}
          </Typography>
        </IconButton>
      </Tooltip>

      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 4,
            // MuiPaper's default background.paper (MUI's stock #121212 in dark
            // mode) is darker than the page background and doesn't match the
            // app's greydark surface scale — set it explicitly to line up
            // with the rest of the dark-mode UI.
            ...(dark && { bgcolor: 'greydark.C800', border: '1px solid', borderColor: 'greydark.C600' }),
          },
        }}
      >
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            selected={language === lang.code}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <span style={{ fontSize: '1.2rem' }}>{lang.flag}</span>
              <ListItemText primary={lang.label} />
              {language === lang.code && (
                <ListItemIcon sx={{ minWidth: 'auto' }}>
                  <Check size={16} />
                </ListItemIcon>
              )}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/* FILESTRUCTURE
src/
├── contexts/
│   └── I18nContext.jsx
├── components/
│   └── LanguageSelector.jsx
├── locales/
│   ├── en/
│   │   └── translationEN.js
│   └── de/
│       └── translationDE.js
└── App.jsx
*/